import React from "react";
import Link from "next/link";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocSection,
  DocCard,
  DocAlert,
  DocNavFooter,
} from "@/components/docs";
import {
  EnvelopeIcon,
  GlobeAltIcon,
  ChatBubbleLeftRightIcon,
  ServerStackIcon,
  FireIcon,
  BoltIcon,
  BellAlertIcon,
  TicketIcon,
} from "@heroicons/react/24/outline";
import { docsColors, docsComponents } from "@/lib/docs-theme";

const notificationMethods = [
  {
    title: "Email",
    description: "Reliable email alerts with delivery to multiple recipients",
    icon: EnvelopeIcon,
    href: "/docs/notifications/email",
    features: [
      "Instant delivery to multiple recipients",
      "Daily email limit matches your plan",
      "Plain text format for compatibility",
      "Downtime duration in recovery alerts",
    ],
  },
  {
    title: "Webhooks",
    description: "Send alerts to any HTTP endpoint with JSON payloads",
    icon: GlobeAltIcon,
    href: "/docs/notifications/webhooks",
    features: [
      "Custom HTTP endpoints",
      "JSON payload format",
      "Real-time delivery",
      "Build custom integrations",
    ],
  },
  {
    title: "Slack",
    description: "Real-time notifications in your team's Slack channels",
    icon: ChatBubbleLeftRightIcon,
    href: "/docs/notifications/slack",
    features: [
      "Simple webhook URL setup",
      "Plain text notifications",
      "Clickable monitor links",
      "Certificate expiration warnings",
    ],
  },
  {
    title: "Discord",
    description: "Send alerts to Discord servers for team notifications",
    icon: ServerStackIcon,
    href: "/docs/notifications/discord",
    features: [
      "Simple webhook URL setup",
      "Plain text notifications",
      "Clickable monitor links",
      "Certificate expiration warnings",
    ],
  },
  {
    title: "Microsoft Teams",
    description: "Send alerts to Microsoft Teams channels using Workflow webhooks",
    icon: ChatBubbleLeftRightIcon,
    href: "/docs/notifications/ms-teams",
    features: [
      "Adaptive Card format",
      "Clickable monitor links",
      "Certificate expiration warnings",
      "Teams Workflow webhook support",
    ],
  },
  {
    title: "PagerDuty",
    description: "Create incidents in PagerDuty for on-call management",
    icon: FireIcon,
    href: "/docs/notifications/pagerduty",
    features: [
      "Automatic incident creation",
      "Auto-resolve when monitors recover",
      "Deduplication of alerts",
      "Events API v2 integration",
    ],
  },
  {
    title: "incident.io",
    description: "Modern incident response with HTTP Alert Events API",
    icon: BoltIcon,
    href: "/docs/notifications/incident-io",
    features: [
      "HTTP Alert Events API",
      "Auto-resolve when monitors recover",
      "Monitor metadata in alerts",
      "Certificate expiration alerts",
    ],
  },
  {
    title: "Opsgenie",
    description: "Advanced alerting and on-call management by Atlassian",
    icon: BellAlertIcon,
    href: "/docs/notifications/opsgenie",
    features: [
      "Alert API v2 integration",
      "Auto-close when monitors recover",
      "Customizable priority levels (P1-P5)",
      "US & EU data center support",
    ],
  },
  {
    title: "Zendesk",
    description: "Create and manage support tickets automatically",
    icon: TicketIcon,
    href: "/docs/notifications/zendesk",
    features: [
      "Automatic ticket creation",
      "Auto-solve when monitors recover",
      "Deduplication using external IDs",
      "Custom tags and priority levels",
    ],
  },
];

export default function NotificationsOverviewPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "Notification Methods" },
  ];

  return (
    <DocLayout
      title="Notification Methods - Uptime Monitor Documentation"
      description="Configure how you receive alerts when monitors detect issues. Email, webhooks, Slack, Discord, Microsoft Teams, PagerDuty, incident.io, Opsgenie, and Zendesk."
      canonical="https://uptime-monitor.io/docs/notifications"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Notification Methods"
        description="Configure how you receive alerts when monitors detect issues. Choose from email, webhooks, Slack, Discord, Microsoft Teams, PagerDuty, incident.io, Opsgenie, Zendesk, and more to ensure critical alerts reach the right people."
      />

      {/* How Notifications Work */}
      <DocSection>
        <DocAlert variant="info" className="p-6">
          <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>
            How Notifications Work
          </h2>
          <div className={`space-y-3 ${docsColors.text.body}`}>
            <p>
              When a monitor detects an issue or recovers, notifications are sent to all assigned
              contacts:
            </p>
            <ul className="space-y-2 ml-6">
              <li className="flex items-start">
                <span className="text-blue-600 dark:text-blue-400 mr-2">1.</span>
                Monitor detects status change (online → offline or vice versa)
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 dark:text-blue-400 mr-2">2.</span>
                System verifies the issue from multiple locations to avoid false positives
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 dark:text-blue-400 mr-2">3.</span>
                Notifications sent to all assigned contacts simultaneously
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 dark:text-blue-400 mr-2">4.</span>
                Recovery notifications sent when service comes back online
              </li>
            </ul>
          </div>
        </DocAlert>
      </DocSection>

      {/* All Notification Methods */}
      <DocSection title="Available Notification Methods">
        <div className="grid gap-6">
          {notificationMethods.map((method) => (
            <DocCard key={method.href}>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <method.icon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="ml-4 flex-1">
                  <h3 className={`text-2xl font-semibold ${docsColors.text.heading} mb-2`}>
                    {method.title}
                  </h3>
                  <p className={`${docsColors.text.body} mb-4`}>{method.description}</p>

                  <div className="mb-4">
                    <h4
                      className={`text-sm font-semibold ${docsColors.text.muted} uppercase tracking-wider mb-2`}
                    >
                      Key Features
                    </h4>
                    <ul className={`grid sm:grid-cols-2 gap-1 text-sm ${docsColors.text.body}`}>
                      {method.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <span className={`${docsColors.text.subtle} mr-2`}>•</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link
                    href={method.href}
                    className={`inline-flex items-center ${docsColors.link.primary} font-medium cursor-pointer`}
                  >
                    Configure {method.title} →
                  </Link>
                </div>
              </div>
            </DocCard>
          ))}
        </div>
      </DocSection>

      {/* Contact Management */}
      <DocSection title="Managing Notification Contacts">
        <DocCard>
          <div className="space-y-4">
            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Adding Contacts</h3>
              <p className={`${docsColors.text.body} mb-2`}>
                Create notification contacts in your account settings:
              </p>
              <ul className={`space-y-1 text-sm ${docsColors.text.body} ml-6`}>
                <li>• Navigate to Settings → Contacts</li>
                <li>• Choose notification type (Email, Webhook, Slack, etc.)</li>
                <li>• Enter required configuration (email address, webhook URL, etc.)</li>
                <li>• Assign contacts when creating or editing monitors</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                Contact Assignment
              </h3>
              <p className={`${docsColors.text.body} mb-2`}>
                Each monitor can have multiple contacts assigned:
              </p>
              <ul className={`space-y-1 text-sm ${docsColors.text.body} ml-6`}>
                <li>• Mix different notification types for redundancy</li>
                <li>• Email for detailed reports, PagerDuty for urgent alerts</li>
                <li>• Webhooks and integrations for automated workflows</li>
                <li>• Update assignments anytime without disrupting monitoring</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Contact Limits</h3>
              <ul className={`space-y-1 text-sm ${docsColors.text.body} ml-6`}>
                <li>• Unlimited contacts of all types</li>
                <li>
                  • Email has daily limits (matches your plan&apos;s monitor count, more with
                  prepaid credits)
                </li>
                <li>• Webhooks and integrations are unlimited</li>
              </ul>
            </div>
          </div>
        </DocCard>
      </DocSection>

      {/* Notification Content */}
      <DocSection title="Notification Content">
        <DocCard>
          <p className={`${docsColors.text.body} mb-4`}>
            All notifications include essential information about the incident:
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                Down Alerts Include:
              </h3>
              <ul className={`space-y-1 text-sm ${docsColors.text.body}`}>
                <li className="flex items-center">
                  <span className="text-red-600 dark:text-red-400 mr-2">•</span>
                  Monitor name and URL
                </li>
                <li className="flex items-center">
                  <span className="text-red-600 dark:text-red-400 mr-2">•</span>
                  Error type and details
                </li>
                <li className="flex items-center">
                  <span className="text-red-600 dark:text-red-400 mr-2">•</span>
                  Time of failure
                </li>
                <li className="flex items-center">
                  <span className="text-red-600 dark:text-red-400 mr-2">•</span>
                  Response code or error message
                </li>
                <li className="flex items-center">
                  <span className="text-red-600 dark:text-red-400 mr-2">•</span>
                  Link to monitor dashboard
                </li>
              </ul>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                Recovery Alerts Include:
              </h3>
              <ul className={`space-y-1 text-sm ${docsColors.text.body}`}>
                <li className="flex items-center">
                  <span className="text-green-600 dark:text-green-400 mr-2">•</span>
                  Monitor name and URL
                </li>
                <li className="flex items-center">
                  <span className="text-green-600 dark:text-green-400 mr-2">•</span>
                  Recovery confirmation
                </li>
                <li className="flex items-center">
                  <span className="text-green-600 dark:text-green-400 mr-2">•</span>
                  Total downtime duration
                </li>
                <li className="flex items-center">
                  <span className="text-green-600 dark:text-green-400 mr-2">•</span>
                  Recovery time
                </li>
                <li className="flex items-center">
                  <span className="text-green-600 dark:text-green-400 mr-2">•</span>
                  Link to monitor dashboard
                </li>
              </ul>
            </div>
          </div>
        </DocCard>
      </DocSection>

      {/* Credits and Billing */}
      <DocSection title="Notification Credits & Limits">
        <DocCard>
          <div className="space-y-4">
            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                Email Notifications
              </h3>
              <ul className={`space-y-2 ${docsColors.text.body}`}>
                <li className="flex items-start">
                  <span className={`${docsColors.text.muted} mr-2`}>•</span>
                  <div>
                    <strong className={docsColors.text.heading}>
                      Daily limits reset every 24 hours
                    </strong>{" "}
                    at noon UTC
                  </div>
                </li>
                <li className="flex items-start">
                  <span className={`${docsColors.text.muted} mr-2`}>•</span>
                  <div>
                    <strong className={docsColors.text.heading}>Daily limit:</strong> Matches your
                    plan&apos;s monitor count (e.g., 50 monitor plan = 50 emails/day)
                  </div>
                </li>
                <li className="flex items-start">
                  <span className={`${docsColors.text.muted} mr-2`}>•</span>
                  <div>Additional email packages available: 1,000 / 3,000 / 10,000 credits</div>
                </li>
              </ul>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                Other Notification Types
              </h3>
              <p className={docsColors.text.body}>
                <strong className={docsColors.text.heading}>Unlimited:</strong> Webhooks, Slack,
                Discord, Microsoft Teams, PagerDuty, incident.io, Opsgenie, and Zendesk have no
                credit requirements or sending limits.
              </p>
            </div>
          </div>

          <DocAlert variant="warning" className="mt-4">
            <p className={`text-sm ${docsColors.text.body}`}>
              <strong className={docsColors.text.heading}>Out of credits?</strong> You&apos;ll
              receive one notification alerting you that your limit has been reached. Monitor status
              changes will still be recorded, but notifications won&apos;t be sent until credits are
              available.
            </p>
          </DocAlert>
        </DocCard>
      </DocSection>

      {/* Best Practices */}
      <DocSection title="Notification Best Practices">
        <DocCard>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Use multiple notification types</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Combine email and webhooks for critical monitors, add Slack for team awareness
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>
                  Leverage incident management tools
                </strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Use PagerDuty, incident.io, or Opsgenie for production systems requiring on-call
                  rotations
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Test notifications regularly</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Use the Test button next to each contact to verify notification delivery is
                  working correctly
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Use webhooks for automation</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Build custom integrations to trigger automated response workflows
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Monitor credit usage</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Track email credit usage to ensure you don&apos;t run out during critical times
                </p>
              </div>
            </li>
          </ul>
        </DocCard>
      </DocSection>

      <DocNavFooter
        prev={{ href: "/docs/monitors/ping", text: "Ping Monitoring" }}
        next={{
          href: "/docs/notifications/email",
          text: "Email Notifications",
        }}
      />
    </DocLayout>
  );
}
